'use client';

import { type ScopeCerItem, ScopeSelector as ScopeSelectorUi } from '@slims/ui/organisms';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchEnergyMembers, energyMembersKey } from '@/hooks/use-energy-members';
import { fetchScopedConfig, scopedConfigKey } from '@/hooks/use-scoped-config';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useScopeStore } from '@/stores/scope-store';
import { useAuthStore } from '@/stores/auth-store';

export function ScopeSelector() {
	const { tree, loading } = useScopeTree();
	const role = useAuthStore((s) => s.role);
	const selectedOrgIds = useScopeStore((s) => s.selectedOrgIds);
	const setSelection = useScopeStore((s) => s.setSelection);
	const queryClient = useQueryClient();

	const uiTree: ScopeCerItem[] = useMemo(
		() =>
			tree
				// Only show CERs that have at least one configuration.
				.filter((cer) => cer.configs.length > 0)
				.map((cer) => ({
					id: cer.id,
					nome: cer.nome,
					configs: cer.configs.map((c) => ({ id: c.id, nome: c.nome, soci: c.soci })),
				})),
		[tree],
	);

	// ADR-006: al passaggio del puntatore su una configurazione, pre-carica i suoi
	// dati universali (config + soci) così il pannello è già caldo al click.
	// `prefetchQuery` non fa nulla se l'entry è già fresca in cache.
	const peekConfig = useCallback(
		(orgId: string) => {
			queryClient.prefetchQuery({
				queryKey: scopedConfigKey(orgId),
				queryFn: () => fetchScopedConfig(orgId),
			});
			queryClient.prefetchQuery({
				queryKey: energyMembersKey(orgId),
				queryFn: () => fetchEnergyMembers(orgId),
			});
		},
		[queryClient],
	);

	if (loading || uiTree.length === 0) return null;
	if (role === 'member' && uiTree.reduce((count, cer) => count + cer.configs.length, 0) <= 1) return null;

	return (
		<ScopeSelectorUi
			tree={uiTree}
			selectedIds={selectedOrgIds}
			onApply={setSelection}
			onPeekConfig={peekConfig}
		/>
	);
}
